// i18n: translated
/**
 * アプリケーションヘッダー
 *
 * MOCK v2.7 準拠のヘッダー構成:
 * 左: ハンバーガーメニュー + ロゴ + サブテキスト
 * 右: 表示言語切替 + テーマ切替 + ヘルプ + 対応状況 + 不具合報告 + 通知ベル + 事務所名 + ロールバッジ + ユーザーメニュー
 *
 * SPEC 7.1.1 通知ドロワー連携。
 * 表示言語 (日本語 / English) の切替は SPEC 7.1.0t / ADR-081 (admin-english-toggle-20260924)。
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LOCALE_NATIVE_NAMES, useLocale, useT } from '@/i18n';
import { shellNs } from '@/i18n/namespaces/shell';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { useLocalMcpStatus } from '@/hooks/useLocalMcpStatus';
import { CURRENT_MCP_VERSION } from '@/utils/mcpClientVersion';
import {
  useOrganization,
  useCurrentUserProfile,
  CURRENT_USER_PROFILE_CHANGED_EVENT,
  type CurrentUserProfileChangedDetail,
} from '@/hooks/useAdmin';
import NotificationBell from '@/components/layout/NotificationBell';
import MyTasksBadge from '@/components/layout/MyTasksBadge';
import UserAvatar from '@/components/common/UserAvatar';
import { Badge } from '@/components/common/Badge';
import { ROLE_BADGE_VARIANT, ROLE_LABELS } from '@/utils/adminUserLabels';
import { resolveHelpAnchor } from '@/utils/helpAnchors';
import {
  resolveAvatarInitialSeed,
  resolveHeaderDisplayName,
  resolveUserRoleLabel,
} from '@/utils/userDisplay';
import type { BadgeVariant } from '@/types';
import logoMainUrl from '@/assets/logos/logo-main.svg?url';
import logoMainDarkUrl from '@/assets/logos/logo-main-dark.svg?url';

/**
 * 役割 pill の表記と色。**ユーザー管理テーブルのロール列と同じ** (`ROLE_LABELS` / `ROLE_BADGE_VARIANT`:
 * Owner=red / Admin=blue / Member=green / Viewer=gray) にする (高橋 2026-09-06「実際のロール名に合わせるべき」)。
 * 既定ロール以外 (カスタムロール等) は `resolveUserRoleLabel` の表記 (raw 値の素通し) を gray で出す。
 */
function resolveRoleBadge(role: string | null | undefined): {
  label: string;
  variant: BadgeVariant;
} {
  const key = typeof role === 'string' ? role.trim() : '';
  if (Object.prototype.hasOwnProperty.call(ROLE_LABELS, key)) {
    const known = key as keyof typeof ROLE_LABELS;
    return { label: ROLE_LABELS[known], variant: ROLE_BADGE_VARIANT[known] };
  }
  return { label: resolveUserRoleLabel(role), variant: 'gray' };
}

// ==============================
// Props
// ==============================

interface HeaderProps {
  /** サイドバー折りたたみトグル */
  onSidebarToggle: () => void;
}

// ==============================
// Header コンポーネント
// ==============================

export default function Header({ onSidebarToggle }: HeaderProps) {
  const t = useT(shellNs);
  const { locale, setLocale } = useLocale();
  // 切替先の言語。ボタンには **切替先の言語名をその言語自身の表記で** 出す
  // (日本語を読めない職員でも「English」を見つけられる / 英語表示中は「日本語」で戻れる)。
  const nextLocale = locale === 'ja' ? 'en' : 'ja';
  const nextLocaleName = LOCALE_NATIVE_NAMES[nextLocale];
  const { user, logout } = useAuth();
  const localMcp = useLocalMcpStatus(
    !!user && user.orgId !== 'PLATFORM' && user.role !== 'superadmin',
    `${user?.orgId ?? ''}:${user?.userId ?? ''}`,
  );
  const { canRead } = usePermission();
  const { data: orgData } = useOrganization(canRead('orgSettings'));
  // ログイン中ユーザー本人の profile (DynamoDB displayName / avatarUrl) を取得 (未ログイン時は fetch しない)
  const { data: profile, refetch: refetchProfile } = useCurrentUserProfile(!!user);
  // プロフィール写真 (user-profile-avatar-20260828)。未設定・解決失敗時は null。
  const avatarUrl = profile?.avatarUrl ?? null;
  // 写真が無いときのイニシャル円の元になる氏名 (photo-kanji-display-20260830)。
  // **表示名 (displayName) をそのまま渡さない** — あちらは氏名が無いと email local-part まで
  // 落ちるため、渡すとメール先頭 1 文字が出てしまう。氏名が取れないときは undefined を返し、
  // UserAvatar が従来どおり人物アイコンへ fallback する。
  const avatarInitialSeed = resolveAvatarInitialSeed(profile?.displayName, user?.name);
  const orgLogoUrl = orgData?.logoUrl ?? null;
  const orgName = orgData?.orgName ?? profile?.orgName ?? user?.orgName ?? '';
  // 氏名は profile の displayName (DynamoDB / ユーザー管理一覧と同じ正本) を最優先。
  // 未取得時は JWT `name` → email local-part の順にフォールバックする (#275 挙動を維持)。
  const displayName = resolveHeaderDisplayName(profile?.displayName, user?.name, user?.email);
  // role の正式日本語ラベル (バッジの title / ドロップダウン表示に使用)
  // 役割 pill (ユーザー管理テーブルと同じ表記・色)
  const roleBadge = resolveRoleBadge(user?.role);
  // カスタムロールも、自己プロフィールで解決済みの表示名を使う。
  if (profile?.access?.status === 'ok' && profile.access.roleId === user?.role) {
    roleBadge.label = profile.access.roleName;
  }
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** テーマ初期化: localStorage から読み込み */
  useEffect(() => {
    const stored = localStorage.getItem('acecollect_theme');
    if (stored === 'dark') {
      document.documentElement.classList.add('dark');
      setDarkMode(true);
    } else if (stored === 'light') {
      document.documentElement.classList.remove('dark');
      setDarkMode(false);
    } else {
      // デフォルトはダークモード
      const isDark = document.documentElement.classList.contains('dark');
      setDarkMode(isDark);
    }
  }, []);

  /** テーマ切替 */
  const toggleTheme = useCallback(() => {
    const isDark = document.documentElement.classList.toggle('dark');
    setDarkMode(isDark);
    localStorage.setItem('acecollect_theme', isDark ? 'dark' : 'light');
  }, []);

  /**
   * 自分のプロフィールが更新されたら再取得する (user-profile-avatar-20260828)。
   *
   * `useFetch` は component ごとに独立した state なので、ユーザー管理画面側の `refetch()` は
   * ここには届かない。**編集対象が自分自身のときだけ** 発火する CustomEvent で橋渡しし、
   * ページ reload や再ログインなしで右上の写真を差し替える。
   */
  useEffect(() => {
    if (!user) return;
    const onProfileChanged = (e: Event) => {
      const detail = (e as CustomEvent<CurrentUserProfileChangedDetail>).detail;
      // 他ユーザーの編集では発火させない (無関係な再取得を増やさない)。
      if (!detail?.userId || detail.userId !== profile?.userId) return;
      refetchProfile();
    };
    window.addEventListener(CURRENT_USER_PROFILE_CHANGED_EVENT, onProfileChanged);
    return () => window.removeEventListener(CURRENT_USER_PROFILE_CHANGED_EVENT, onProfileChanged);
  }, [user, profile?.userId, refetchProfile]);

  /** メニュー外クリックで閉じる */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  /** ログアウト処理 */
  const handleLogout = useCallback(async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  return (
    <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800">
      {/* 左: ハンバーガーメニュー + ロゴ */}
      <div className="flex items-center gap-3">
        {/* ハンバーガーメニュー */}
        <button
          type="button"
          onClick={onSidebarToggle}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label={t('header.toggleSidebar')}
        >
          {/* Heroicons: bars-3 */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>

        {/* ブランドロゴ — 納品済み横長ロゴを theme で出し分け (AceCollect 文字を含むため text span は不要) */}
        <div className="flex items-center gap-2 whitespace-nowrap">
          <img src={logoMainUrl} alt="AceCollect" className="h-6 w-auto dark:hidden" />
          <img
            src={logoMainDarkUrl}
            alt=""
            aria-hidden="true"
            className="hidden h-6 w-auto dark:block"
          />
          <span className="hidden text-xs text-gray-600 dark:text-gray-300 md:inline">
            {t('header.tagline')}
          </span>
        </div>
      </div>

      {/* 右: 表示言語切替 + テーマ切替 + ヘルプ + 不具合報告 + 通知ベル + 事務所名 + ユーザーメニュー (役割はメニュー内の pill) */}
      <div className="flex items-center gap-2">
        {/* この端末の新しい応答で旧版を確認できた場合だけ表示する。 */}
        {localMcp.kind === 'outdated' && (
          <a
            href="/mcp/update-guide.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-mcp-update"
            title={t('header.mcpUpdateTitle', {
              version: localMcp.version,
              current: CURRENT_MCP_VERSION,
            })}
            aria-label={t('header.mcpUpdateTitle', {
              version: localMcp.version,
              current: CURRENT_MCP_VERSION,
            })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.992 0a9 9 0 0 0 15.357 3.329M21.015 9.348a9 9 0 0 0-15.357-3.329"
              />
            </svg>
            <span className="hidden md:inline">{t('header.mcpUpdate')}</span>
          </a>
        )}
        {/* MCP の利用履歴は別端末・コネクタも共有するため、この端末の更新警告には使わない。 */}
        {/* 表示言語切替 (admin-english-toggle-20260924 / ADR-081)。押すたびに 日本語 ⇔ English。
            ラベルは切替先の言語名 (その言語自身の表記)。依頼者・案件のデータは翻訳しない。 */}
        <button
          type="button"
          onClick={() => setLocale(nextLocale)}
          data-testid="header-language-toggle"
          aria-label={t('language.switchTo', { language: nextLocaleName })}
          title={t('language.switchTo', { language: nextLocaleName })}
          className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          {/* Heroicons: language */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802"
            />
          </svg>
          {/* 言語名だけを切替先の言語として読ませる (ボタンの読み上げ名は表示中の言語のまま) */}
          <span className="hidden sm:inline" lang={nextLocale}>
            {nextLocaleName}
          </span>
        </button>

        {/* テーマ切替ボタン */}
        <button
          type="button"
          onClick={toggleTheme}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label={darkMode ? t('header.switchToLight') : t('header.switchToDark')}
        >
          {darkMode ? (
            /* Heroicons: sun (ダークモード時 → 太陽アイコン表示) */
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
              />
            </svg>
          ) : (
            /* Heroicons: moon (ライトモード時 → 月アイコン表示) */
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
              />
            </svg>
          )}
        </button>

        {/* ヘルプボタン — 現在の画面に対応するマニュアル章へ遷移 (B2 contextual help) */}
        <button
          type="button"
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label={t('header.help')}
          title={t('header.helpTitle')}
          onClick={() => navigate(`/help#${resolveHelpAnchor(location.pathname, locale)}`)}
        >
          {/* Heroicons: question-mark-circle */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
            />
          </svg>
        </button>

        {/* 対応状況ボタン (request-status-20261001) — 依頼・不具合の対応状況 (/request-status) へ。
            ヘルプと不具合報告の間に置く (報告した後の状況をここで確かめる) */}
        <button
          type="button"
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label={t('header.requestStatus')}
          title={t('header.requestStatusTitle')}
          data-testid="header-request-status"
          onClick={() => navigate('/request-status')}
        >
          {/* Heroicons: clipboard-document-list */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
            />
          </svg>
        </button>

        {/* 不具合報告ボタン — 開いた時点の画面 (path + search) を遷移元として渡す。
            origin / hash は付けない (機密値を含めない・full URL を送らない方針)。 */}
        <button
          type="button"
          onClick={() =>
            navigate('/bug-report', {
              state: { sourcePath: `${location.pathname}${location.search}` },
            })
          }
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          aria-label={t('header.bugReport')}
        >
          {/* Heroicons: exclamation-triangle */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-5 w-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </button>

        {/* 自分のタスク (未完了件数バッジ、steering 627) */}
        <MyTasksBadge />

        {/* 通知ベル */}
        <NotificationBell />

        {/* 区切り線 */}
        <div className="mx-1 h-6 w-px bg-gray-200 dark:bg-gray-700" />

        {/* 事務所ロゴ + 事務所名 + ユーザー名 */}
        <div className="hidden items-center gap-1.5 md:flex">
          {orgLogoUrl && <img src={orgLogoUrl} alt="" className="h-5 w-5 rounded object-contain" />}
          <span className="text-sm text-gray-600 dark:text-gray-400">{orgData?.orgName ?? ''}</span>
        </div>

        {/* ユーザーメニュー (header-user-menu-redesign 2026-09-05)
            ボタン = avatar + 氏名 + chevron (開いている間は上向き)。ヘッダーの単字ロールバッジは廃止し、
            役割はドロップダウンの「現在の組織」カードに pill で出す。 */}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setShowUserMenu((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={showUserMenu}
            aria-label={t('header.userMenu')}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm transition-colors ${
              showUserMenu
                ? 'border-gray-300 bg-gray-100 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
                : 'border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
            }`}
          >
            {/* プロフィール写真 → 氏名の 1 文字目 → 人物アイコン の順に落ちる。
                氏名が取れない (email しか無い) ユーザーでは従来どおり人物アイコン。 */}
            <UserAvatar
              avatarUrl={avatarUrl}
              sizeClass="h-6 w-6 text-[11px]"
              initialsSeed={avatarInitialSeed}
            />
            <span className="hidden sm:inline">{displayName}</span>
            {/* Heroicons: chevron-down (開いている間は rotate-180 で上向き) */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`h-4 w-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </button>

          {/* ドロップダウンメニュー: 氏名 + メール / 現在の組織カード (組織名 + 役割 pill) / 個人設定 / ログアウト */}
          {showUserMenu && (
            <div
              role="menu"
              aria-label={t('header.userMenu')}
              className="absolute right-0 top-full z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-2 shadow-2xl dark:border-gray-700 dark:bg-gray-900"
            >
              {/* 見出し: 大きめ avatar + 氏名 + メール */}
              <div className="flex items-center gap-3 px-2 py-2">
                <UserAvatar
                  avatarUrl={avatarUrl}
                  sizeClass="h-12 w-12 text-lg"
                  initialsSeed={avatarInitialSeed}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold text-gray-900 dark:text-white">
                    {displayName}
                  </p>
                  <p
                    className="truncate text-xs text-gray-500 dark:text-gray-400"
                    title={user?.email}
                  >
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* 現在の組織 (組織名 + 役割 pill)。役割はユーザー管理テーブルのロール列と同じ表記・色 (Owner / Admin / Member / Viewer)。既定ロール以外は表記を素通しして gray */}
              <div
                data-testid="user-menu-org-card"
                className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/60"
              >
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t('header.currentOrg')}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  {/* Heroicons: building-office */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
                    />
                  </svg>
                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {orgName || t('header.orgNameUnset')}
                  </span>
                </div>
                <span
                  data-testid="user-menu-role-pill"
                  className="mt-2 inline-flex"
                  title={t('header.roleTitle', { role: roleBadge.label })}
                >
                  <Badge variant={roleBadge.variant}>{roleBadge.label}</Badge>
                </span>
              </div>

              <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

              {/* 個人設定 */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowUserMenu(false);
                  navigate('/preferences');
                }}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              >
                <span className="flex items-center gap-3">
                  {/* Heroicons: cog-6-tooth */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                  {t('header.preferences')}
                </span>
                {/* Heroicons: chevron-right */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4 text-gray-400"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              </button>

              <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

              {/* ログアウト (rose) */}
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
              >
                <span className="flex items-center gap-3">
                  {/* Heroicons: arrow-right-on-rectangle */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                    />
                  </svg>
                  {t('header.logout')}
                </span>
                {/* Heroicons: chevron-right */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4 text-rose-400"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m8.25 4.5 7.5 7.5-7.5 7.5"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
